/**
 * `<RevokeInvitationDialog>` — confirm before revoking a pending
 * invitation.
 */

import { authClient, type AuthClientError } from "@cometchat-ai/auth/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { formatApiError } from "../lib/format-error";

export interface RevokeInvitationDialogProps {
  workspaceSlug: string;
  invitationId: string;
  invitationEmail: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RevokeInvitationDialog({
  workspaceSlug,
  invitationId,
  invitationEmail,
  open,
  onOpenChange,
}: RevokeInvitationDialogProps) {
  const { t } = useTranslation("workspace");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => authClient.revokeInvitation(workspaceSlug, invitationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invitations", workspaceSlug] });
      toast.success(t("members.revoke.toast"));
      onOpenChange(false);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("members.revoke.failed"), { description: formatApiError(err) });
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={`revoke-invitation-dialog-${invitationId}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("members.revoke.confirmTitle", { email: invitationEmail })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t("members.revoke.confirmBody")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={mutation.isPending}
            data-testid={`revoke-invitation-cancel-${invitationId}`}
          >
            {t("members.revoke.confirmNo")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              mutation.mutate();
            }}
            disabled={mutation.isPending}
            data-testid={`revoke-invitation-confirm-${invitationId}`}
          >
            {t("members.revoke.confirmYes")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
